import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomInputSelect from "../../FormFields/CustomSelect/CustomInputSelect";
import MultiSelect from "../../FormFields/CustomSelect/MultiSelect";
import {
  createReviewNotes,
  getReviewNotes,
} from "../../../api/services/communication";
import { useQuery, useQueryClient } from "react-query";
import { getScheduleJobDetails } from "../../../api/services/scheduling";
import { QK } from "../../../base/qk";
import { useSnackbar } from "notistack";
import { designationOptions } from "../dashboard.config";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";

const ReviewNoteModal = ({ jobId, open, handleClose }) => {
  const [noteData, setNoteData] = useState({
    reciever_type: "individual",
    candidate_list: [],
  });
  const [notes, setNotes] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: notesData } = useQuery(
    [QK.notesData, jobId],
    () => getReviewNotes(jobId),
    { enabled: !!jobId }
  );
  const { data: jobData } = useQuery(
    [QK.scheduleJobDetails, jobId],
    () => getScheduleJobDetails(jobId),
    { enabled: !!jobId }
  );

  useEffect(() => {
    if (notesData) {
      setNotes(notesData);
    }
  }, [notesData]);

  useEffect(() => {
    if (jobData?.employee_details) {
      const formattedOptions = jobData.employee_details.map((staff) => ({
        value: staff.id,
        label: `${staff.first_name} ${staff.last_name}`,
      }));
      setStaffOptions(formattedOptions);
      const preselected = formattedOptions.filter((option) =>
        jobData.employee_list.includes(option.value)
      );
      setSelectedStaff(preselected);
      handleInputChange(
        "candidate_list",
        preselected.map((opt) => opt.value)
      );
    }
  }, [jobData]);

  const handleInputChange = (field, value) => {
    setNoteData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNoteChange = (event) => {
    handleInputChange("note", event.target.value);
  };

  const handleReceiverTypeChange = (event) => {
    const receiverType = event.target.value;
    handleInputChange("reciever_type", receiverType);
  };

  const handleDesignationChange = (event) => {
    handleInputChange("designation", event.target.value);
  };

  const handleStaffSelection = (selectedOptions) => {
    setSelectedStaff(selectedOptions);
    handleInputChange(
      "candidate_list",
      selectedOptions.map((opt) => opt.value)
    );
  };

  const handleSubmit = async (noteType) => {
    const dataToSubmit = {
      ...noteData,
      note_type: noteType,
      scheduled_job: jobId,
    };
    try {
      const response = await createReviewNotes(dataToSubmit);
      if (response.status === 201) {
        enqueueSnackbar("Note saved successfully", { variant: "success" });
        queryClient.invalidateQueries(QK.notesData);
        queryClient.invalidateQueries(QK.candidateNotes);
      }
      setNoteData({
        note: "",
      });
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1, maxHeight: "80vh" } }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#386591",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          pl: 2,
        }}
      >
        <Typography variant="h6">Review Notes</Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
          {notes.length === 0 && (
            <Typography variant="body2" p={5}>
              {" "}
              No Notes Available{" "}
            </Typography>
          )}
          {notes.map((note) => (
            <Box
              key={note.id}
              sx={{
                backgroundColor: "#f5f5f5",
                m: 1,
                p: 2,
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: "#eaeaea",
                    color: "#666",
                    fontSize: "10px",
                    mr: 1,
                  }}
                >
                  {note.sender_name
                    ?.split(" ")
                    .map((name) => name[0])
                    .join("")}
                </Avatar>
                <Typography
                  component="span"
                  variant="subtitle2"
                  color="primary"
                >
                  {note.sender_name}
                </Typography>
              </Box>
              <Typography variant="body2">{note.note}</Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "right",
                  color: "#90CAF9",
                  mt: 1,
                }}
              >
                {new Date(note.created_at).toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Type your note here..."
            variant="outlined"
            value={noteData.note || ""}
            onChange={handleNoteChange}
            sx={{ mb: 1 }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={() => handleSubmit("notes")}
              sx={{ bgcolor: "#1976d2", flex: 1 }}
            >
              Save as Note
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSubmit("message")}
              sx={{ bgcolor: "#1976d2", flex: 1 }}
            >
              Send Message
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSubmit("review_notes")}
              sx={{ bgcolor: "#1976d2", flex: 1, whiteSpace: "nowrap" }}
            >
              Save As Review Note
            </Button>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Send to:
            </Typography>
            <RadioGroup
              name="receiver-type"
              value={noteData.reciever_type}
              onChange={handleReceiverTypeChange}
              row
            >
              <FormControlLabel
                value="all_staff"
                control={<Radio />}
                label="All Staff"
              />
              <FormControlLabel
                value="designated"
                control={<Radio />}
                label="By Designation"
              />
              <FormControlLabel
                value="individual"
                control={<Radio />}
                label="Individual"
              />
            </RadioGroup>

            {noteData.reciever_type === "designated" && (
              <Box sx={{ mt: 1 }}>
                <CustomSelect
                  options={designationOptions}
                  name="designation"
                  selectedValue={noteData.designation}
                  label="Select Designation"
                  onChange={handleDesignationChange}
                />
              </Box>
            )}

            {noteData.reciever_type === "individual" && (
              <Box sx={{ mt: 1 }}>
                <MultiSelect
                  options={staffOptions}
                  value={selectedStaff}
                  onChange={handleStaffSelection}
                  placeholder="Select staff members..."
                  className="basic-multi-select"
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewNoteModal;
